import * as React from "react";
import { Table, ActionButton, Input } from "@jhool-io/fe-components";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
    BillPaymentType,
    IBillingResponse,
} from "../../../billing/types/billing.types";
import { formatDate, showMoneyInAppFormat } from "../../../../utils/helpers";
import {
    IGeneratePaymentLinkInvoicePayload,
    PaymentLinkType,
} from "../../types/clients.types";
import useToast from "../../../../hooks/useToast";
import EditIcon from "../../../../icons/Edit";
import GreenCheckIcon from "../../../../components/Icons/GreenCheck";
import CancelPaymentIcon from "../../../../components/Icons/CancelPayment";

type PaymentLinkBillType = BillPaymentType.CI | BillPaymentType.PR100;

interface PaymentLinkPreviewFormProps {
    onFormSubmit: (
        invoices: IGeneratePaymentLinkInvoicePayload[],
        email?: string
    ) => void;
    selectedInvoices: IBillingResponse[];
    linkType: PaymentLinkType | null;
    onValidationChange?: (isValid: boolean) => void;
}

interface EditableInvoice extends IBillingResponse {
    editedCoinsurance: number | null;
    editedPr100: number | null;
}

interface PaymentLinkFormData {
    email?: string;
}

function getBillTypeRemainder(
    invoice: IBillingResponse,
    billType: PaymentLinkBillType
): number {
    if (billType === BillPaymentType.PR100) {
        return Math.max(invoice.accounting_pr100, 0);
    }
    return Math.max(-invoice.accounting_coinsurance, 0);
}

function getDisplayAmount(
    invoice: EditableInvoice,
    billType: PaymentLinkBillType
): number {
    if (billType === BillPaymentType.PR100) {
        return invoice.editedPr100 ?? getBillTypeRemainder(invoice, billType);
    }
    return invoice.editedCoinsurance ?? getBillTypeRemainder(invoice, billType);
}

function computeColumnVisibility(invoices: EditableInvoice[]): VisibilityState {
    return {
        coinsurance_amount: invoices.some(
            (inv) => getBillTypeRemainder(inv, BillPaymentType.CI) > 0
        ),
        pr100_amount: invoices.some(
            (inv) => getBillTypeRemainder(inv, BillPaymentType.PR100) > 0
        ),
    };
}

function computeTotalAmount(
    invoices: EditableInvoice[],
    visibility: VisibilityState
): number {
    return invoices.reduce((sum, invoice) => {
        const ci =
            visibility.coinsurance_amount === false
                ? 0
                : getDisplayAmount(invoice, BillPaymentType.CI);
        const pr =
            visibility.pr100_amount === false
                ? 0
                : getDisplayAmount(invoice, BillPaymentType.PR100);
        return sum + ci + pr;
    }, 0);
}

export default function PaymentLinkPreviewForm({
    onFormSubmit,
    selectedInvoices,
    linkType,
    onValidationChange,
}: PaymentLinkPreviewFormProps) {
    const schema = React.useMemo(
        () =>
            yup.object({
                email:
                    linkType === PaymentLinkType.CLOSED
                        ? yup
                              .string()
                              .email("Please enter a valid email address")
                              .required("Email is required")
                        : yup.string().optional(),
            }),
        [linkType]
    );

    const {
        register,
        handleSubmit: handleFormSubmit,
        formState: { errors, isValid: isEmailValid },
    } = useForm<PaymentLinkFormData>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            email: "",
        },
    });

    const [editableInvoices, setEditableInvoices] = React.useState<
        EditableInvoice[]
    >(
        selectedInvoices.length > 0
            ? selectedInvoices.map((invoice) => ({
                  ...invoice,
                  editedCoinsurance: null,
                  editedPr100: null,
              }))
            : []
    );
    const [editingCell, setEditingCell] = React.useState<{
        invoiceId: string;
        billType: PaymentLinkBillType;
    } | null>(null);
    const [editingAmount, setEditingAmount] = React.useState<string>("");
    const { toast } = useToast();

    const columnVisibility = React.useMemo<VisibilityState>(
        () => computeColumnVisibility(editableInvoices),
        [editableInvoices]
    );

    const totalAmount = React.useMemo(
        () => computeTotalAmount(editableInvoices, columnVisibility),
        [editableInvoices, columnVisibility]
    );

    const notifyValidity = (
        nextInvoices: EditableInvoice[],
        emailValid: boolean
    ) => {
        const nextVisibility = computeColumnVisibility(nextInvoices);
        const nextTotal = computeTotalAmount(nextInvoices, nextVisibility);
        onValidationChange?.(emailValid && nextTotal > 0);
    };

    const handleEditAmount = (
        invoiceId: string,
        billType: PaymentLinkBillType
    ) => {
        const invoice = editableInvoices.find(
            (inv) => inv.invoice_id === invoiceId
        );
        if (invoice) {
            setEditingCell({ invoiceId, billType });
            setEditingAmount(getDisplayAmount(invoice, billType).toFixed(2));
        }
    };

    const handleSaveAmount = (
        invoiceId: string,
        billType: PaymentLinkBillType
    ) => {
        const amount = parseFloat(editingAmount);
        if (Number.isNaN(amount) || amount <= 0) {
            toast({
                mode: "error",
                message: "Amount must be greater than 0",
            });
            return;
        }
        const invoice = editableInvoices.find(
            (inv) => inv.invoice_id === invoiceId
        );
        if (!invoice) {
            return;
        }
        const remainder = getBillTypeRemainder(invoice, billType);
        if (amount > remainder) {
            toast({
                mode: "error",
                message: `Amount cannot exceed ${showMoneyInAppFormat(
                    remainder
                )}`,
            });
            return;
        }
        const nextInvoices = editableInvoices.map((inv) =>
            inv.invoice_id === invoiceId
                ? {
                      ...inv,
                      editedCoinsurance:
                          billType === BillPaymentType.CI
                              ? amount
                              : inv.editedCoinsurance,
                      editedPr100:
                          billType === BillPaymentType.PR100
                              ? amount
                              : inv.editedPr100,
                  }
                : inv
        );
        setEditableInvoices(nextInvoices);
        setEditingCell(null);
        setEditingAmount("");
        notifyValidity(nextInvoices, isEmailValid);
    };

    const handleCancelEdit = () => {
        setEditingCell(null);
        setEditingAmount("");
    };

    const onSubmit = (data: PaymentLinkFormData) => {
        if (totalAmount <= 0) {
            toast({
                mode: "error",
                message: "Nothing to charge on the selected invoices",
            });
            return;
        }
        const invoicesToSend: IGeneratePaymentLinkInvoicePayload[] =
            editableInvoices.map((invoice) => {
                const payload: IGeneratePaymentLinkInvoicePayload = {
                    invoice_id: invoice.invoice_id,
                };
                if (getBillTypeRemainder(invoice, BillPaymentType.CI) > 0) {
                    payload.coinsurance_amount = getDisplayAmount(
                        invoice,
                        BillPaymentType.CI
                    );
                }
                if (getBillTypeRemainder(invoice, BillPaymentType.PR100) > 0) {
                    payload.pr100_amount = getDisplayAmount(
                        invoice,
                        BillPaymentType.PR100
                    );
                }
                return payload;
            });

        onFormSubmit(
            invoicesToSend,
            linkType === PaymentLinkType.CLOSED ? data.email : undefined
        );
    };

    const renderAmountCell = (
        invoice: EditableInvoice,
        billType: PaymentLinkBillType
    ) => {
        const remainder = getBillTypeRemainder(invoice, billType);
        const isSettled = remainder <= 0;
        const isEditing =
            editingCell?.invoiceId === invoice.invoice_id &&
            editingCell?.billType === billType;

        if (isSettled) {
            return (
                <div className="flex items-center gap-2">
                    <span>{showMoneyInAppFormat(0)}</span>
                    <span className="text-xs text-white bg-primary rounded px-2 py-0.5">
                        Already Settled
                    </span>
                </div>
            );
        }

        if (isEditing) {
            return (
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingAmount}
                        onChange={(e) => setEditingAmount(e.target.value)}
                        className="w-[100px] h-7"
                        showCurrency
                        autoFocus
                    />
                    <ActionButton
                        size="auto"
                        variant="normal"
                        onClick={() =>
                            handleSaveAmount(invoice.invoice_id, billType)
                        }
                        actionTitle="Save amount"
                    >
                        <GreenCheckIcon />
                    </ActionButton>
                    <ActionButton
                        size="auto"
                        variant="normal"
                        onClick={handleCancelEdit}
                        actionTitle="Cancel editing"
                    >
                        <CancelPaymentIcon />
                    </ActionButton>
                </div>
            );
        }

        return (
            <div className="flex items-center gap-2">
                <span>
                    {showMoneyInAppFormat(getDisplayAmount(invoice, billType))}
                </span>
                <ActionButton
                    size="auto"
                    variant="normal"
                    onClick={() =>
                        handleEditAmount(invoice.invoice_id, billType)
                    }
                    actionTitle="Edit amount"
                >
                    <EditIcon className="stroke-text" />
                </ActionButton>
            </div>
        );
    };

    const columns: ColumnDef<EditableInvoice>[] = React.useMemo(
        () => [
            {
                accessorKey: "date_of_service",
                header: "Date of Service",
                cell: ({ row }) =>
                    formatDate(row.original.date_of_service) || "--",
            },
            {
                accessorKey: "provider",
                header: "Provider",
                cell: ({ row }) => {
                    const { provider } = row.original;
                    return provider
                        ? `${provider.first_name} ${provider.last_name}`
                        : "--";
                },
            },
            {
                accessorKey: "coinsurance_amount",
                header: "Session Fee",
                // eslint-disable-next-line react/no-unstable-nested-components
                cell: ({ row }) =>
                    renderAmountCell(row.original, BillPaymentType.CI),
            },
            {
                accessorKey: "pr100_amount",
                header: "PR100",
                // eslint-disable-next-line react/no-unstable-nested-components
                cell: ({ row }) =>
                    renderAmountCell(row.original, BillPaymentType.PR100),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editingCell, editingAmount, editableInvoices]
    );

    return (
        <div className="space-y-4">
            <p className="text-sm text-text-secondary">
                Review the invoices and adjust amounts if needed. Leave amounts
                as is for full balance payment, or edit to specify a partial
                payment amount for the session fee or PR100.
            </p>

            <form
                id="payment-link-preview-form"
                onSubmit={handleFormSubmit(onSubmit)}
            >
                {linkType === PaymentLinkType.CLOSED && (
                    <div className="space-y-2 mb-4">
                        <Input
                            type="email"
                            label="Email address"
                            placeholder="Enter email address to receive verification code"
                            {...register("email", {
                                onChange: async (e) => {
                                    const valid = await yup
                                        .string()
                                        .email()
                                        .required()
                                        .isValid(e.target.value);
                                    notifyValidity(editableInvoices, valid);
                                },
                            })}
                            hasError={!!errors.email}
                            errorText={errors.email?.message}
                            isRequired
                        />
                        <p className="text-xs text-text mb-2">
                            Note: A verification code will be required to use
                            this payment link. Enter the email address where the
                            verification code should be sent.
                        </p>
                    </div>
                )}
            </form>

            <Table
                data={editableInvoices}
                columns={columns}
                isColumnVisible={columnVisibility}
                className="max-h-[400px] overflow-y-auto"
            />

            <div className="flex justify-between items-center pt-4 border-t border-stroke">
                <span className="text-base font-semibold text-text">
                    Total Amount:
                </span>
                <span className="text-title font-bold text-text">
                    {showMoneyInAppFormat(totalAmount)}
                </span>
            </div>
        </div>
    );
}
